import styled, {
	css,
	FlattenSimpleInterpolation as CSS,
} from 'styled-components';
import { rgba } from 'polished';

import * as mx from 'core/styles/mixins';
import { IThemeObject } from 'pods/theme';

import { IProps } from './BackgroundContainer';

/**
 * Applies semi-transparent shade/overlay ontop of the background image.
 *
 * @param {Object} theme - Theme object helper functions.
 * @param {string} bgImageShadeColor - `BrandColor` id identifier - or 'black', 'white'.
 * @param {number} bgImageShadeOpacity - CSS opacity of shade overlay.
 */
const applyBackgroundShade = (
	theme: IThemeObject,
	bgImageShadeColor?: string,
	bgImageShadeOpacity?: number,
): CSS | null => {
	const bgColor =
		(bgImageShadeColor && theme.core.getCompositionColor(bgImageShadeColor)) ||
		bgImageShadeColor;
	const shade = Number(bgImageShadeOpacity);

	return bgColor && bgColor.toLowerCase() !== 'none'
		? css`
				background-color: ${rgba(bgColor, shade)};
		  `
		: null;
};

/**
 * Applies background image, and optional shade overlay color.
 * Also sets appropriate text color.
 *
 * @param {Object} theme - Theme object helper functions.
 * @param {string} bgImage - Background image URL.
 * @param {string} [bgImageShadeColor] - `BrandColor` id identifier - or 'black', 'white'.
 * @param {number} [bgImageShadeOpacity] - Opacity of shade overlay, if applicable.
 * @param {string} [bgImagePosition] - CSS 'background-position' property.
 */
const applyBackgroundImage = (
	theme: IThemeObject,
	bgImage?: string,
	bgImageShadeColor?: string,
	bgImageShadeOpacity?: number,
	bgImagePosition?: string,
): CSS =>
	css`
		&::before,
		&::after {
			${mx.pseudo}
			${mx.square('100%')}
			top: 0;
			left: 0;
			z-index: -1;
			border-radius: inherit;
		}

		&::before {
			background-image: url(${bgImage});
			background-size: cover;
			background-position: ${bgImagePosition};
		}

		${bgImageShadeColor &&
			css`
				${bgImageShadeColor === 'black'
					? css`
							${theme.css.getLightText()}

							&::after {
								${applyBackgroundShade(theme, 'black', bgImageShadeOpacity)}
							}
					  `
					: bgImageShadeColor === 'white'
					? css`
							${theme.css.getDarkText()}

							&::after {
								${applyBackgroundShade(theme, 'white', bgImageShadeOpacity)}
							}
					  `
					: css`
							${theme.css.getText(bgImageShadeColor)}

							&::after {
								${applyBackgroundShade(
									theme,
									bgImageShadeColor,
									bgImageShadeOpacity,
								)}
							}
					  `}
			`}
	`;

/**
 * Applies background color and accompanying text color for children to inherit too.
 *
 * @param {Object} theme - Theme object helper functions.
 * @param {string} bgColor - `BrandColor` name identifier - or any other valid CSS colour string.
 */
const applyBackgroundColor = (
	theme: IThemeObject,
	bgColor?: string,
): CSS | '' =>
	bgColor
		? !theme.util.isBrandColor(bgColor)
			? css`
					/* This block constitutes the user essentially setting
					a 'manual' background colour, as opposed to a 'BrandColor'.
					This can be problematic since we can't know the appropriate
					text color to set. We can hope that they utilise the
					'textColor' override prop to set the corresponding light/dark
					to complement their choice of 'bgColor'; but failing that
					we can make an educated guess to serve as a robust fallback
					by applying the inverse text of the global theme default
					module colour. */
					color: ${theme.global.module.isDark
						? theme.global.typography.colors.genericLight
						: theme.global.typography.colors.genericDark};
					background-color: ${bgColor};
			  `
			: theme.css.getBackgroundAndText(bgColor)
		: '';

export const InlineImgShade = styled.div<{
	bgImageShadeColor?: string;
	bgImageShadeOpacity?: number;
}>`
	position: relative;

	&::before {
		${mx.pseudo}
		${mx.square('100%')}
		top: 0;
		left: 0;

		${({ theme, bgImageShadeColor, bgImageShadeOpacity }) =>
			applyBackgroundShade(theme, bgImageShadeColor, bgImageShadeOpacity)}
	}
`;

export const Background = styled.div<IProps>`
	border-radius: inherit;

	/* Comprehensive defaults */
	${({ isDefaultModule, bgColor, bgImage, theme }) => css<IProps>`
		/* Modules can opt-in to having the 'default' background value of
		this module set to the global module default, as opposed to null/transparent. */
		${isDefaultModule &&
			!bgImage &&
			applyBackgroundColor(theme, theme.global.module.color)}

		/* When there is no background colour or image, apply a default
		text colour for children of this component based on the global
		module background colour. This is so any component utilising
		this module can totally defer text 'color' responsibilities to this
		component, even when no 'bgImage' or 'bgColor' has been set. If
		this default colour is inaccurate it can be overriden with 'textColor' */
		${
			!bgColor && !bgImage && theme.global.module.isDark
				? theme.css.getLightText()
				: theme.css.getDarkText()
		}
	`}

	/* Optionally apply vertical padding when background color/image exists */
	${({ bgColor, bgImage, bgPadding }) =>
		(bgColor || bgImage) &&
		css`
			padding: ${bgPadding};
		`}

	/* Apply background image or color and appropriate text color; image takes priority */
	${({
		bgColor,
		bgImage,
		bgImageShadeColor,
		bgImageShadeOpacity,
		bgImagePosition,
		theme,
	}) =>
		bgImage
			? applyBackgroundImage(
					theme,
					bgImage,
					bgImageShadeColor,
					bgImageShadeOpacity,
					bgImagePosition,
			  )
			: bgColor && applyBackgroundColor(theme, bgColor)}

	/* Optional text color overrides; ONLY applicable
	for background images without a shade/overlay */
	${({ textColor, theme, bgImage, bgImageShadeColor }) =>
		textColor &&
		bgImage &&
		!bgImageShadeColor &&
		(textColor === 'light'
			? theme.css.getLightText()
			: theme.css.getDarkText())}
`;

// Ensures:
// - Absolutely positioned children won't escape component's bounds
// - Nested BackgroundContainer modules will be layered
//   appropriately with relative z-indexes
// - Fill available height of container
export const Wrapper = styled.div<{
	allowChildrenScape?: boolean;
}>`
	${mx.square('100%')}
	position: relative;
	z-index: ${props => (props.allowChildrenScape ? 'auto' : '0')};
	border-radius: inherit;

	&,
	${Background as any} {
		height: 100%;
	}
`;
